import React from "react";
import { Form } from "react-bootstrap";
import { withTranslation, WithTranslation } from "react-i18next";
import C3 from "c3";
import ChannelSettingDialog from "../common/ChannelSettingDialog";
import { IMessageBoxParams } from "../../../../../common/MessageBox";
import { IConfirmDialogParams } from "../../../../../common/ConfirmDialog";
import {
  getFileChannels,
  FileChannelItem,
} from "../../../../../webapi/service/getFileChannels";
import { createModel } from "../../../../../webapi/service/createModel";
import ModelListPanel from "./ModelListPanel";
import AuthService from "../../../../../common/AuthService";
import {
  drawCharts,
  getChartTableInfo,
  ChartSize,
  createChartTableStyle,
  getSortProts,
} from "../common/chart";
import {
  ProjectStatus,
  ModelSelect,
  ChartList,
} from "../../../../../common/constants";
import { IProjectRow } from "../../../../../webapi/service/getProjects";
import { IProtRow } from "../../../../../webapi/service/getFIles";
import "../../projectdetail.css";
import { WebAPI } from "../../../../../webapi/constants";

export interface ICreateModelPanelProps {
  /**
   * 認証サービス
   */
  authService?: AuthService;
  /**
   * メインスペースの幅
   */
  mainSpaceWidth: number;
  /**
   * 選択されたプロジェクト情報
   */
  projectInfo: IProjectRow;
  /**
   * チャート描画情報一覧
   */
  prots: IProtRow[];
  /**
   * チャート描画順
   */
  sortIds: number[];
  /**
   * ファイル一覧画面に戻る場合に呼び出します
   */
  onBack: () => void;
  /**
   * プロジェクト一覧画面に戻る場合に呼び出します
   */
  onTop: () => void;
  /**
   * ローディング画面の表示非表示を切り替えます
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoading: (loading: boolean) => void;
  /**
   * メッセージBoxを表示します
   * @param params - メッセージBoxパラメータ
   */
  showMessage: (params: IMessageBoxParams) => void;
  /**
   * 確認ダイアログを表示します。
   * @param params - 確認ダイアログパラメータ
   */
  showConfirm: (params: IConfirmDialogParams) => void;
  /**
   * 通信エラーが発生した場合に呼び出します
   * @param id - メッセージID
   * @param status - httpステータスコード
   */
  onCommunicationError: (
    id: string | undefined,
    status: number | undefined
  ) => void;
  /**
   * 非操作タイムアウトをリセットする場合に呼び出します
   */
  resetUnoperatedTimeout: () => void;
}

export interface IProps extends ICreateModelPanelProps, WithTranslation {}

export interface ILabelSelectItem {
  /**
   * ID
   */
  id: number;
  /**
   * ファイルID
   */
  fileid: string;
  /**
   * 選択ID
   */
  selectid: string;
  /**
   * 選択値。
   * モデルの場合はconstants.tsxのModelSelect参照。
   * プロファイルの場合はconstants.tsxのProfileSelect参照。
   */
  value: number;
}

interface IState {
  /**
   * チャート一覧
   */
  selectItems: ILabelSelectItem[];
  /**
   * チャネル情報一覧
   */
  channels: FileChannelItem[];
  /**
   * OKファイル一覧
   */
  okfiles: string[];
  /**
   * NGファイル一覧
   */
  ngfiles: string[];
  /**
   * チャネル設定ダイアログを呼び出す場合はtrue
   */
  showChannelSetting: boolean;
  /**
   * モデルが作成可能な場合はtrue
   */
  create: boolean;
}

/**
 * チャート一覧を生成します
 * @param {IProtRow[]} prots - チャートデータ一覧
 * @returns チャート一覧
 */
export function createLabelSelectItems(prots: IProtRow[]): ILabelSelectItem[] {
  return prots.map((prot) => {
    return {
      id: prot.id,
      fileid: prot.fileid,
      selectid: "select_" + prot.id,
      value: 0,
    };
  });
}

/**
 * チャート一覧からOK,NGファイルの一覧を取得します
 * @param selectItems - チャート一覧
 * @returns OK,NGファイル一覧
 */
export function collectLabelFileids(selectItems: ILabelSelectItem[]): {
  okfilesids: string[];
  ngfilesids: string[];
} {
  const okfiles: string[] = [];
  const ngfiles: string[] = [];
  selectItems.forEach((item) => {
    switch (item.value) {
      case ModelSelect.OK:
        okfiles.push(item.fileid);
        break;
      case ModelSelect.NG:
        ngfiles.push(item.fileid);
        break;
    }
  });
  return { okfilesids: okfiles, ngfilesids: ngfiles };
}

function isCreateEnabled(selectItems: ILabelSelectItem[]): boolean {
  const ok = selectItems.find((item) => {
    return item.value === ModelSelect.OK;
  });
  const ng = selectItems.find((item) => {
    return item.value === ModelSelect.NG;
  });
  return ok && ng ? true : false;
}

/**
 * チャート一覧の選択値を更新します
 * @param selectItems - チャート一覧
 * @param id - 選択ID
 * @param value - 選択値
 * @returns チャート一覧
 */
export function changeLabelSelectItemValue(
  selectItems: ILabelSelectItem[],
  id: string,
  value: number
): ILabelSelectItem[] {
  selectItems.forEach((item) => {
    if (item.selectid === id) {
      item.value = value;
    }
  });
  return selectItems;
}

/**
 * チャート一覧の選択値を一括更新します
 * @param selectItems - チャート一覧
 * @param value - 選択値
 * @returns チャート一覧
 */
export function changeAllLabelSelectItemValue(
  selectItems: ILabelSelectItem[],
  value: number
): ILabelSelectItem[] {
  selectItems.forEach((item) => {
    item.value = value;
  });
  return selectItems;
}

export interface IOptionItems {
  /**
   * 選択項目
   */
  text: string;
  /**
   * 選択値
   */
  value: number;
}

/**
 * 表示選択項目一覧取得します
 * @param optionItems - 選択項目一覧
 * @returns 表示選択項目一覧
 */
export function getOptionItems(optionItems: IOptionItems[]): JSX.Element[] {
  return optionItems.map((item, i) => {
    return (
      <option value={item.value} key={i}>
        {item.text}
      </option>
    );
  });
}

/**
 * 各チャートの選択項目一覧を取得します
 * @param protId - ID
 * @param selectItems - チャート一覧
 * @param options - 表示選択項目一覧
 * @param onChange - 選択項目が変更された場合に呼び出されます
 * @returns 各チャートの選択項目一覧
 */
export function getSelectList(
  protId: number,
  selectItems: ILabelSelectItem[],
  options: JSX.Element[],
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
): JSX.Element | "" {
  const item = selectItems.find((item) => {
    return item.id === protId;
  });
  return item ? (
    <Form.Select
      className="item"
      id={item.selectid}
      value={item.value}
      onChange={onChange}
    >
      {options}
    </Form.Select>
  ) : (
    ""
  );
}

/**
 * チャート一覧を生成します
 * @param {IProtRow[]} prots - チャートデータ一覧
 * @param {ILabelSelectItem[]} selectItems - チャート一覧
 * @param {string} chartidFmt - チャートIDフォーマット
 * @param {IOptionItems[]} optionItems - 選択項目一覧
 * @param {(e: React.ChangeEvent<HTMLSelectElement>)} onChange - 選択項目が変更された場合に呼び出されます
 * @returns チャート一覧
 */
export function createChartTable(
  prots: IProtRow[],
  selectItems: ILabelSelectItem[],
  chartidFmt: string,
  optionItems: IOptionItems[],
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
): JSX.Element[] {
  const options = getOptionItems(optionItems);
  const charts = prots.map((prot, i) => {
    const { id } = prot;
    const chartid: string = chartidFmt + String(id);
    const formselect = getSelectList(id, selectItems, options, onChange);
    return (
      <div className="chart-list-item" key={i}>
        <div id={chartid}></div>
        <div className="chart-list-select">{formselect}</div>
      </div>
    );
  });
  return charts;
}

class CreateModelPanel extends React.Component<IProps, IState> {
  private readonly chartid_fmt = "chart_create_model_";
  private readonly DefaultChartSize: ChartSize = {
    width: ChartList.MODEL_WIDTH,
    height: ChartList.MODEL_HEIGHT,
  };
  private readonly MaxDataNum = ChartList.MAX_DATA_NUM;
  private readonly StatusDone = ProjectStatus.DONE;
  private _charts: C3.ChartAPI[] | null = null;
  private _chartSize: ChartSize = this.DefaultChartSize;

  constructor(props: IProps) {
    super(props);

    this.state = {
      selectItems: [],
      channels: [],
      okfiles: [],
      ngfiles: [],
      showChannelSetting: false,
      create: false,
    };

    this.handleCreate = this.handleCreate.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleChangeBulk = this.handleChangeBulk.bind(this);
    this.handleOpenChannelSetting = this.handleOpenChannelSetting.bind(this);
    this.handleChannelSettingOK = this.handleChannelSettingOK.bind(this);
    this.handleChannelSettingCancel =
      this.handleChannelSettingCancel.bind(this);
    this.chartSize = this.chartSize.bind(this);
  }

  chartSize(size?: ChartSize) {
    if (size) {
      this._chartSize = size;
    }
    return this._chartSize;
  }

  createCharts(prots: IProtRow[]): void {
    const { mainSpaceWidth } = this.props;
    const { chartWidth, chartHeight } = getChartTableInfo(
      mainSpaceWidth,
      this.DefaultChartSize,
      true
    );
    this._charts = drawCharts(prots, this.chartid_fmt, chartWidth, chartHeight);
    this.chartSize({ width: chartWidth, height: chartHeight });
  }

  destroyCharts(): void {
    if (this._charts) {
      this._charts.forEach((chart) => {
        chart.destroy();
      });
      this._charts = null;
    }
  }

  getPros() {
    const { prots, sortIds } = this.props;
    return getSortProts(prots, sortIds).filter((prot) => {
      return prot.datanum <= this.MaxDataNum;
    });
  }

  componentDidMount() {
    const { onLoading } = this.props;
    setTimeout(() => {
      const prots = this.getPros();
      this.createCharts(prots);
      this.setState({ selectItems: createLabelSelectItems(prots) });
      onLoading(false);
    });
    onLoading(true);
  }

  componentWillUnmount() {
    this.destroyCharts();
  }

  handleCreate() {
    const { projectInfo, showConfirm, t } = this.props;
    if (projectInfo.mcreatests === this.StatusDone) {
      showConfirm({
        title: t("model_create_overwrite"),
        message: t("model_create_overwrite_message"),
        onOk: () => {
          this.handleOpenChannelSetting();
        },
      });
    } else {
      this.handleOpenChannelSetting();
    }
  }

  handleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const id = e.target.id;
    const value: number = parseInt(e.target.value);
    const selectItems = changeLabelSelectItemValue(
      this.state.selectItems.slice(),
      id,
      value
    );
    this.setState({
      selectItems: selectItems,
      create: isCreateEnabled(selectItems),
    });
  }

  handleChangeBulk(mode: number) {
    const selectItems = changeAllLabelSelectItemValue(
      this.state.selectItems.slice(),
      mode
    );
    this.setState({
      selectItems: selectItems,
      create: isCreateEnabled(selectItems),
    });
  }

  handleOpenChannelSetting() {
    const { authService, projectInfo, onLoading, onCommunicationError } =
      this.props;
    onLoading(true);
    const { okfilesids, ngfilesids } = collectLabelFileids(
      this.state.selectItems
    );
    const fileids = okfilesids.concat(ngfilesids);
    getFileChannels({
      authService: authService,
      projectid: projectInfo.projectid,
      fileids,
      success: (channels) => {
        if (channels.length === 0) {
          onCommunicationError(WebAPI.ID_NO_COMMON_CHANNEL, undefined);
        } else {
          this.setState({
            showChannelSetting: true,
            channels: channels,
            okfiles: okfilesids,
            ngfiles: ngfilesids,
          });
          onLoading(false);
        }
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  handleChannelSettingOK(channels: string[]) {
    const {
      authService,
      projectInfo,
      onLoading,
      onTop,
      onCommunicationError,
      resetUnoperatedTimeout,
    } = this.props;
    const { okfiles, ngfiles } = this.state;
    onLoading(true);
    this.handleChannelSettingCancel();
    resetUnoperatedTimeout();
    createModel({
      authService: authService,
      projectid: projectInfo.projectid,
      okfiles: okfiles,
      ngfiles: ngfiles,
      channels: channels,
      success: () => {
        onTop();
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  handleChannelSettingCancel() {
    this.setState({ showChannelSetting: false });
  }

  render() {
    const { mainSpaceWidth, onBack, t } = this.props;
    const { selectItems, create, showChannelSetting, channels } = this.state;
    const chartTableStyle = createChartTableStyle(
      mainSpaceWidth,
      this.DefaultChartSize,
      this._charts,
      this.chartSize
    );
    const optionItems: IOptionItems[] = [
      { text: t("model_settings_ok"), value: ModelSelect.OK },
      { text: t("model_settings_ng"), value: ModelSelect.NG },
      {
        text: t("model_settings_unused"),
        value: ModelSelect.UNUSED,
      },
    ];
    const contents = createChartTable(
      this.getPros(),
      selectItems,
      this.chartid_fmt,
      optionItems,
      this.handleChange
    );
    return (
      <React.Fragment>
        <ModelListPanel
          label={t("model_create")}
          chartTableStyle={chartTableStyle}
          chartTable={contents}
          isCreateEnabled={create}
          onBack={onBack}
          onCreate={this.handleCreate}
          onChangeAll={this.handleChangeBulk}
        />
        <ChannelSettingDialog
          title={t("model_settings_channel")}
          caption={t("model_settings_channel_caption1")}
          show={showChannelSetting}
          channels={channels}
          onOk={this.handleChannelSettingOK}
          onCancel={this.handleChannelSettingCancel}
        />
      </React.Fragment>
    );
  }
}

/**
 * モデル作成画面
 */
export default withTranslation()(CreateModelPanel);
